/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useEffect, useCallback, memo } from 'react';
import CompanyContext from './CompanyContext';
import initialCompanyState from './initialCompanyState';
import companyReducer from 'contexts/company/reducer/company.reducer';
import * as action from 'contexts/company/reducer/company.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';
import useContextUser from 'hooks/contexts/useContextUser';
import {
  ICompanyUpdateFields,
  IselectCompanySuperAdminProps,
  IupdateSocialMediaProps,
} from 'interfaces/company.interface';

function CompanyProvider(props: IChildrenProps) {
  const [state, dispatch] = useReducer(companyReducer, initialCompanyState);
  const { getAccessTokenSilently } = useAuth0();
  const { dbUser } = useContextUser();
  const { t: translate } = useTranslation();
  const { exchanges } = useContextGeneral();

  useEffect(() => {
    (async () => {
      if (
        dbUser?.user_tenants &&
        dbUser.user_tenants[0]?.tenant?.tenant_id &&
        !state.company
      ) {
        const token = await getAccessTokenSilently();
        if (!token) return false;
        return await action.getCompanyDataAction({
          dispatch,
          tenantId: dbUser.user_tenants[0]?.tenant?.tenant_id,
          countryId: dbUser.user_tenants[0]?.tenant?.country_id,
          token,
          translate,
        });
      }
    })();
  }, [state.company, dbUser?.user_tenants]);

  const updateCompanyData = useCallback(
    async ({ companyData }: { companyData: ICompanyUpdateFields }) => {
      if (exchanges.length === 0 || !state.company) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateCompanyAction({
        dispatch,
        companyState: state.company,
        companyData,
        exchanges,
        token,
        translate,
      });
    },
    [state.company, exchanges]
  );

  const updateCompanyLogo = useCallback(
    async (image: File) => {
      if (!state.company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateCompanyLogoAction({
        dispatch,
        tenantId: state.company.id,
        image,
        token,
        translate,
      });
    },
    [state.company?.id]
  );

  const updateSocialMedia = useCallback(
    async ({ socialMedia }: IupdateSocialMediaProps) => {
      if (!state.company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateSocialMediaAction({
        dispatch,
        companyState: state.company,
        socialMedia,
        token,
        translate,
      });
    },
    [state.company]
  );

  const removeSocialMedia = useCallback(
    async ({ socialMediaId }: { socialMediaId: string }) => {
      if (!state.company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeSocialMediaAction({
        dispatch,
        companyState: state.company,
        socialMediaId,
        token,
        translate,
      });
    },
    [state.company]
  );

  const selectCompanySuperAdmin = useCallback(
    async ({ tenant }: IselectCompanySuperAdminProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.selectCompanySuperAdminAction({
        dispatch,
        tenant,
        translate,
        token,
      });
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...state,
      updateCompanyData,
      updateCompanyLogo,
      updateSocialMedia,
      removeSocialMedia,
      selectCompanySuperAdmin,
    }),
    [
      state,
      updateCompanyData,
      updateCompanyLogo,
      updateSocialMedia,
      removeSocialMedia,
      selectCompanySuperAdmin,
    ]
  );

  return (
    <CompanyContext.Provider value={memoProvider}>
      {props.children}
    </CompanyContext.Provider>
  );
}

export default memo(CompanyProvider);
